/**
 * Taboo PDP interactivity: thumbnail gallery, variant selection syncing price,
 * quantity stepper, AJAX add-to-cart with a temporary "Added ✓" state,
 * and keeping the sticky mobile buy bar in sync with the form above it.
 * The add-to-cart button label shows the running total ("Add to cart · $19.99")
 * and updates live when quantity or variant changes, per the design spec.
 *
 * Pre-order products (tagged "pre-order", or relabeled by the PreProduct app)
 * use "Pre-order" wording on both the main button and the sticky mobile bar.
 * The sticky bar clicks the main button rather than submitting the form
 * directly, so PreProduct's click handling runs exactly as it does for the
 * main button.
 *
 * After a successful add, refreshCartDrawer() re-fetches the cart-drawer
 * section via Shopify's Section Rendering API and opens it via
 * <theme-drawer>'s public open() method. This form bypasses the theme's
 * native product-form-component, so nothing else in the theme is told the
 * cart changed unless we do it ourselves here.
 */
if (!customElements.get('taboo-pdp')) {
  class TabooPDP extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-pdp-form]');
      this.qtyValue = this.querySelector('[data-qty-value]');
      this.qtyInput = this.querySelector('[data-qty-input]');
      this.qtyUpBtns = this.querySelectorAll('[data-qty-up]');
      this.qtyDownBtns = this.querySelectorAll('[data-qty-down]');
      this.variantRadios = this.querySelectorAll('[data-variant-radio]');
      this.priceEl = this.querySelector('[data-price]');
      this.stickyPriceEl = this.querySelector('[data-sticky-price]');
      this.addBtn = this.querySelector('[data-add-btn]');
      this.addLabel = this.querySelector('[data-add-label]');
      this.stickyAddBtn = this.querySelector('[data-sticky-add]');
      this.thumbs = this.querySelectorAll('[data-thumb]');
      this.galleryImage = this.querySelector('[data-gallery-image]');

      this.quantity = 1;
      this.isPreorder = false;
      // Price in cents from data attribute; updated on variant change.
      this.currentPriceCents = this.addBtn
        ? parseInt(this.addBtn.dataset.priceCents || '0', 10)
        : 0;

      this.qtyUpBtns.forEach((btn) => btn.addEventListener('click', () => this.setQuantity(this.quantity + 1)));
      this.qtyDownBtns.forEach((btn) => btn.addEventListener('click', () => this.setQuantity(this.quantity - 1)));

      this.variantRadios.forEach((radio) => {
        radio.addEventListener('change', () => this.onVariantChange(radio));
      });

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.onThumbClick(thumb));
      });

      if (this.form) {
        this.form.addEventListener('submit', (e) => this.onSubmit(e));
      }

      if (this.stickyAddBtn && this.addBtn) {
        this.stickyAddBtn.addEventListener('click', () => {
          if (this.addBtn.disabled) return;
          // Click the real button so any app listening for clicks on it
          // (e.g. PreProduct) handles the sticky bar the same way.
          this.addBtn.click();
        });
      }

      this.detectPreorder();
    }

    /**
     * Pre-order if the main button has been relabeled to "pre-order" (by the
     * PreProduct app) or the product carries the "pre-order" tag.
     */
    detectPreorder() {
      if (!this.addBtn) return;

      const buttonSaysPreorder = () => /pre-?order/i.test(this.addBtn.textContent || '');

      if (buttonSaysPreorder()) {
        this.setPreorder();
        return;
      }

      const observer = new MutationObserver(() => {
        if (buttonSaysPreorder()) {
          observer.disconnect();
          this.setPreorder();
        }
      });
      observer.observe(this.addBtn, { childList: true, subtree: true, characterData: true });

      const productUrl = this.dataset.productUrl;
      if (productUrl) {
        fetch(productUrl + '.js', { headers: { Accept: 'application/json' } })
          .then((res) => (res.ok ? res.json() : null))
          .then((product) => {
            const tags = (product && product.tags) || [];
            if (tags.some((tag) => String(tag).toLowerCase() === 'pre-order')) {
              observer.disconnect();
              this.setPreorder();
            }
          })
          .catch(() => {});
      }
    }

    setPreorder() {
      if (this.isPreorder) return;
      this.isPreorder = true;
      if (this.stickyAddBtn && !this.stickyAddBtn.disabled) {
        this.stickyAddBtn.textContent = 'Pre-order';
      }
    }

    formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }

    addBtnLabel() {
      const verb = this.isPreorder ? 'Pre-order' : 'Add to cart';
      return verb + ' · ' + this.formatMoney(this.currentPriceCents * this.quantity);
    }

    setQuantity(value) {
      this.quantity = Math.max(1, value);
      if (this.qtyValue) this.qtyValue.textContent = this.quantity;
      if (this.qtyInput) this.qtyInput.value = this.quantity;
      if (this.addLabel && !this.addBtn.classList.contains('is-added')) {
        this.addLabel.textContent = this.addBtnLabel();
      }
    }

    onVariantChange(radio) {
      const price = radio.dataset.price;
      const priceCents = parseInt(radio.dataset.priceCents || '0', 10);

      if (price) {
        if (this.priceEl) this.priceEl.textContent = price;
        if (this.stickyPriceEl) this.stickyPriceEl.textContent = price;
      }
      if (priceCents) {
        this.currentPriceCents = priceCents;
        if (this.addLabel && !this.addBtn.classList.contains('is-added')) {
          this.addLabel.textContent = this.addBtnLabel();
        }
      }

      this.variantRadios.forEach((r) => {
        const label = r.closest('label');
        if (label) label.classList.toggle('is-selected', r === radio);
      });
    }

    onThumbClick(thumb) {
      const fullSrc = thumb.dataset.fullSrc;
      if (fullSrc && this.galleryImage) {
        this.galleryImage.src = fullSrc;
      }
      this.thumbs.forEach((t) => t.classList.toggle('is-active', t === thumb));
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.form || !this.addBtn) return;

      const formData = new FormData(this.form);
      const originalLabel = this.addBtnLabel();

      this.addBtn.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });

        if (!response.ok) {
          throw new Error('Add to cart failed');
        }

        await response.json();

        if (this.addLabel) this.addLabel.textContent = 'Added ✓';
        this.addBtn.classList.add('is-added');
        if (window.navigator && window.navigator.vibrate) window.navigator.vibrate(20);

        document.dispatchEvent(new CustomEvent('taboo:cart-updated'));

        // This custom form bypasses the theme's native product-form-component,
        // so nothing else (cart drawer, header count) knows the cart changed
        // unless we tell it ourselves.
        this.refreshCartDrawer();

        window.setTimeout(() => {
          if (this.addLabel) this.addLabel.textContent = originalLabel;
          this.addBtn.classList.remove('is-added');
          this.addBtn.disabled = false;
        }, 1500);
      } catch (err) {
        this.addBtn.disabled = false;
        // Fall back to a full page submit if the AJAX call fails for any reason
        this.form.submit();
      }
    }

    /**
     * Re-fetches the cart-drawer section via the Section Rendering API and
     * swaps it into the page, then opens the drawer via <theme-drawer>'s
     * public open() method. If your header cart count/badge lives in a
     * different section, add its id to SECTION_IDS below.
     */
    async refreshCartDrawer() {
      const SECTION_IDS = 'cart-drawer-section,sections--27418588283184__header_section';

      try {
        const res = await fetch(window.location.pathname + '?sections=' + SECTION_IDS);
        if (!res.ok) throw new Error('Section refresh failed');
        const sections = await res.json();

        Object.keys(sections).forEach((id) => {
          const target = document.getElementById('shopify-section-' + id);
          if (!target) return;
          const parsed = new DOMParser().parseFromString(sections[id], 'text/html');
          const replacement = parsed.getElementById('shopify-section-' + id);
          if (replacement) target.replaceWith(replacement);
        });

        const drawer = document.getElementById('cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
        }
      } catch (err) {
        // Non-fatal: the item was already added successfully even if this fails.
        console.warn('[taboo-pdp] Could not refresh/open cart drawer:', err);
      }
    }
  }

  customElements.define('taboo-pdp', TabooPDP);
}
